/**
 * Handwriting Generation Engine
 *
 * Main entry point for the browser-based handwriting generation system.
 * Replaces the original Python backend (`handright` library) with pure
 * JavaScript Canvas API implementation.
 *
 * Inspired by the `handright` Python library by 0x0000FF (MIT License).
 */

import { getBundledFonts, loadUploadedFont, loadFontFromUrl, waitForFont } from './fonts.js';
import { calculateLayout, estimatePages, DEFAULT_LAYOUT } from './layout.js';
import { renderPage, renderAllPages, renderPreview, createNotebookBackground, DEFAULT_PERTURBATION } from './renderer.js';
import { exportZIP, exportPDF, downloadPNG, canvasToDataURL } from './exporter.js';

/**
 * Generate handwriting images from text.
 *
 * @param {object} params - Generation parameters
 * @param {string} params.text - The text to render
 * @param {object} params.template - Layout and perturbation template
 * @param {boolean} [params.preview=false] - If true, only render first page
 * @param {File} [params.fontFile] - User-uploaded font file
 * @param {File|string} [params.backgroundImage] - Background image (File object or data URL)
 * @param {function} [params.onProgress] - Progress callback (stage, message, progress)
 * @returns {Promise<{pages: HTMLCanvasElement[], totalPages: number}>}
 */
export async function generateHandwriting(params) {
  const {
    text,
    template = {},
    preview = false,
    fontFile = null,
    backgroundImage = null,
    onProgress = null,
  } = params;

  const report = (stage, message, progress) => {
    if (onProgress) {
      onProgress({ stage, message, progress });
    }
  };

  report('preparing', '正在准备字体...', 5);

  // Determine font family
  let fontFamily = 'sans-serif';
  if (template.fontFamily) {
    fontFamily = template.fontFamily;
  } else if (fontFile) {
    report('loading_font', '正在加载字体文件...', 10);
    try {
      const { name } = await loadUploadedFont(fontFile);
      fontFamily = name;
    } catch (err) {
      console.error('Failed to load font, using fallback:', err);
    }
  }

  report('loading_font', '字体已就绪', 20);

  // Prepare background
  let bgImage = null;
  if (backgroundImage) {
    report('loading_bg', '正在加载背景图片...', 25);
    try {
      bgImage = await loadImage(backgroundImage);
    } catch (err) {
      console.error('Failed to load background image:', err);
    }
  }

  report('rendering', '正在生成手写图像...', 30);

  // Render pages
  const renderOpts = {
    background: bgImage,
  };

  const mergedTemplate = {
    ...DEFAULT_LAYOUT,
    ...DEFAULT_PERTURBATION,
    fontFamily,
    ...template,
  };

  let result;
  if (preview) {
    report('rendering', '正在生成预览...', 50);
    const canvas = await renderPreview(text, mergedTemplate, renderOpts);
    result = { pages: [canvas], totalPages: 1 };
  } else {
    report('rendering', '正在生成所有页面...', 40);
    result = await renderAllPages(text, mergedTemplate, renderOpts);
  }

  report('done', '生成完成', 100);
  return result;
}

/**
 * Load an image from a File object or URL string.
 *
 * @param {File|string} source - Image source
 * @returns {Promise<HTMLImageElement>}
 */
function loadImage(source) {
  return new Promise((resolve, reject) => {
    const img = new Image();

    if (source instanceof File) {
      const reader = new FileReader();
      reader.onload = (e) => {
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = e.target.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(source);
    } else if (typeof source === 'string') {
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = source;
    } else {
      reject(new Error('Invalid image source'));
    }
  });
}

/**
 * Analyze a background image to detect margins automatically.
 * (Replaces backend /api/imagefileprocess)
 *
 * @param {File|HTMLImageElement} image - The image to analyze
 * @param {number} [sampleRate=10] - How often to sample pixels
 * @returns {Promise<{marginLeft: number, marginRight: number, marginTop: number, marginBottom: number, lineSpacing: number}>}
 */
export async function detectImageMargins(image, sampleRate = 10) {
  const img = image instanceof File ? await loadImage(image) : image;

  const canvas = document.createElement('canvas');
  canvas.width = img.width;
  canvas.height = img.height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0);

  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const { data, width, height } = imageData;

  // Detect left margin
  let marginLeft = 0;
  for (let x = 0; x < width; x += sampleRate) {
    let hasContent = false;
    for (let y = 0; y < height; y += sampleRate) {
      const idx = (y * width + x) * 4;
      const brightness = (data[idx] + data[idx + 1] + data[idx + 2]) / 3;
      if (brightness < 200) {
        hasContent = true;
        break;
      }
    }
    if (hasContent) {
      marginLeft = x;
      break;
    }
  }

  // Detect right margin
  let marginRight = 0;
  for (let x = width - 1; x >= 0; x -= sampleRate) {
    let hasContent = false;
    for (let y = 0; y < height; y += sampleRate) {
      const idx = (y * width + x) * 4;
      const brightness = (data[idx] + data[idx + 1] + data[idx + 2]) / 3;
      if (brightness < 200) {
        hasContent = true;
        break;
      }
    }
    if (hasContent) {
      marginRight = width - x;
      break;
    }
  }

  // Detect top margin
  let marginTop = 0;
  for (let y = 0; y < height; y += sampleRate) {
    let hasContent = false;
    for (let x = 0; x < width; x += sampleRate) {
      const idx = (y * width + x) * 4;
      const brightness = (data[idx] + data[idx + 1] + data[idx + 2]) / 3;
      if (brightness < 200) {
        hasContent = true;
        break;
      }
    }
    if (hasContent) {
      marginTop = y;
      break;
    }
  }

  // Detect bottom margin
  let marginBottom = 0;
  for (let y = height - 1; y >= 0; y -= sampleRate) {
    let hasContent = false;
    for (let x = 0; x < width; x += sampleRate) {
      const idx = (y * width + x) * 4;
      const brightness = (data[idx] + data[idx + 1] + data[idx + 2]) / 3;
      if (brightness < 200) {
        hasContent = true;
        break;
      }
    }
    if (hasContent) {
      marginBottom = height - y;
      break;
    }
  }

  // Estimate line spacing from horizontal line detection
  const lineSpacing = estimateLineSpacing(data, width, height, sampleRate);

  return {
    marginLeft,
    marginRight,
    marginTop,
    marginBottom,
    lineSpacing,
  };
}

/**
 * Estimate line spacing by detecting horizontal lines/rows of content.
 */
function estimateLineSpacing(data, width, height, sampleRate) {
  const rowDarkness = [];
  for (let y = 0; y < height; y += sampleRate) {
    let darkness = 0;
    for (let x = 0; x < width; x += sampleRate) {
      const idx = (y * width + x) * 4;
      darkness += 255 - (data[idx] + data[idx + 1] + data[idx + 2]) / 3;
    }
    rowDarkness.push({ y, darkness });
  }

  // Find peaks in darkness (where text lines are)
  const peaks = [];
  for (let i = 1; i < rowDarkness.length - 1; i++) {
    if (
      rowDarkness[i].darkness > rowDarkness[i - 1].darkness &&
      rowDarkness[i].darkness > rowDarkness[i + 1].darkness &&
      rowDarkness[i].darkness > 50
    ) {
      peaks.push(rowDarkness[i].y);
    }
  }

  if (peaks.length < 2) {
    return 200; // Default line spacing
  }

  // Calculate average distance between consecutive peaks
  let totalDistance = 0;
  for (let i = 1; i < peaks.length; i++) {
    totalDistance += peaks[i] - peaks[i - 1];
  }

  return Math.round(totalDistance / (peaks.length - 1));
}

/**
 * Extract text from a file (.txt, .docx).
 *
 * @param {File} file - The file to read
 * @returns {Promise<string>} Extracted text
 */
export async function extractTextFromFile(file) {
  const name = file.name.toLowerCase();

  if (name.endsWith('.txt') || name.endsWith('.rtf')) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = reject;
      reader.readAsText(file, 'UTF-8');
    });
  }

  if (name.endsWith('.docx')) {
    // For .docx, we need a library like mammoth.js
    // Return a message indicating this requires an additional library
    throw new Error(
      'DOCX support requires mammoth.js. Please install it or use .txt files.'
    );
  }

  if (name.endsWith('.pdf')) {
    throw new Error(
      'PDF text extraction requires pdf.js. Please use .txt files for now.'
    );
  }

  throw new Error(`Unsupported file type: ${name}`);
}

// Re-export for convenience
export {
  getBundledFonts,
  loadUploadedFont,
  loadFontFromUrl,
  waitForFont,
  calculateLayout,
  estimatePages,
  renderPage,
  renderAllPages,
  renderPreview,
  createNotebookBackground,
  exportZIP,
  exportPDF,
  downloadPNG,
  canvasToDataURL,
  DEFAULT_LAYOUT,
  DEFAULT_PERTURBATION,
};
